import { useState } from 'react';
import toast from 'react-hot-toast';
import { targetApi } from '../../services/api';
import { useUnitStore } from '../../store/unitStore';
import { apiErrorMessage } from '../../lib/apiError';
import GeometryPicker from './GeometryPicker';
import { TARGET_TYPES, TARGET_TYPE_META, type Geometry, type Target, type TargetType } from '../../types/domain';

const field = 'w-full mt-0.5 px-2 py-1.5 rounded bg-command-bg border border-command-border text-sm text-gray-100';
const lbl = 'text-[11px] text-gray-400 font-tactical';

// ISO <-> datetime-local helpers.
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function TargetForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Target | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { units } = useUnitStore();
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<TargetType>(initial?.type ?? 'STATIONARY');
  const [enemyManpower, setEnemyManpower] = useState<number>(initial?.enemyManpower ?? 0);
  const [openingTime, setOpeningTime] = useState(toLocalInput(initial?.openingTime));
  const [closingTime, setClosingTime] = useState(toLocalInput(initial?.closingTime));
  const [unitId, setUnitId] = useState(initial?.unitId ?? '');
  // Geometry is NESTED on the DTO (§17): seed straight from `initial.geometry`.
  // (The old flat-field reconstruction read undefined props and showed an empty picker
  // after a successful save.)
  const [geometry, setGeometry] = useState<Geometry | null>(initial?.geometry ?? null);
  const [busy, setBusy] = useState(false);

  const requiredOp = TARGET_TYPE_META[type].operation;
  const requiredShape = TARGET_TYPE_META[type].shape;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    // A target must have a location (§17 requires geometry). The picker emits null until placed.
    if (!geometry) {
      toast.error(`Place the target location on the map (${requiredShape}).`);
      return;
    }
    setBusy(true);
    try {
      // Geometry is a NESTED object on the wire (§15/§17): `geometry: {…}` sets it,
      // `geometry: null` clears it. Never spread the fields flat onto the body.
      const body = {
        name: name.trim(),
        type,
        enemyManpower,
        openingTime: toIso(openingTime),
        closingTime: toIso(closingTime),
        unitId: unitId || null,
        geometry: geometry ?? null,
      };
      if (initial) await targetApi.update(initial.id, body);
      else await targetApi.create(body);
      toast.success(`Target ${initial ? 'updated' : 'created'}`);
      onSaved();
    } catch (err: any) {
      // Surface the first validation detail so the toast is actionable.
      toast.error(apiErrorMessage(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className={lbl}>
          Name
          <input className={field} value={name} onChange={(e) => setName(e.target.value)} aria-label="Target name" />
        </label>
        <label className={lbl}>
          Type
          <select className={field} value={type} onChange={(e) => setType(e.target.value as TargetType)} aria-label="Target type">
            {TARGET_TYPES.map((t) => (
              <option key={t} value={t}>{TARGET_TYPE_META[t].label} → {TARGET_TYPE_META[t].operation}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="text-[11px] text-gray-500">
        {TARGET_TYPE_META[type].icon} {TARGET_TYPE_META[type].label} target — operation <span className="text-command-accent font-semibold">{requiredOp}</span>, drawn as <span className="text-command-accent font-semibold">{requiredShape}</span>.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className={lbl}>
          Enemy manpower
          <input type="number" min={0} className={field} value={enemyManpower} onChange={(e) => setEnemyManpower(Number(e.target.value))} aria-label="Enemy manpower" />
        </label>
        <label className={lbl}>
          Opening time
          <input type="datetime-local" className={field} value={openingTime} onChange={(e) => setOpeningTime(e.target.value)} aria-label="Opening time" />
        </label>
        <label className={lbl}>
          Closing time
          <input type="datetime-local" className={field} value={closingTime} onChange={(e) => setClosingTime(e.target.value)} aria-label="Closing time" />
        </label>
      </div>
      <label className={lbl}>
        Unit (optional)
        <select className={field} value={unitId} onChange={(e) => setUnitId(e.target.value)} aria-label="Unit">
          <option value="">— none —</option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>{u.code}</option>
          ))}
        </select>
      </label>
      <div>
        <div className={lbl}>Geometry</div>
        <GeometryPicker value={geometry} onChange={setGeometry} shape={requiredShape} />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2.5 sm:py-1.5 rounded-lg text-sm text-gray-400 border border-command-border touch-manipulation">Cancel</button>
        <button type="submit" disabled={busy} className="px-5 py-2.5 sm:py-1.5 rounded-lg text-sm font-semibold bg-command-accent text-command-bg disabled:opacity-50 touch-manipulation">
          {busy ? 'Saving…' : 'Save Target'}
        </button>
      </div>
    </form>
  );
}
